/* ---------------------------------------------------------------------------
   MediaRecorder plumbing: capture one blob of microphone audio while some other
   work runs (in our case, playing a muted sub-chunk to its end).

   Kept separate from the recorder state machine because MediaRecorder's
   lifecycle is fiddly enough on its own: `start` and `stop` are events, not
   promises, Safari sometimes never fires `start`, and the mime type the browser
   actually used can differ from the one you asked for.
   --------------------------------------------------------------------------- */

import { RecorderError } from "./recorderTypes";

/** Safari fires `start` late or not at all; don't hang the round waiting. */
const START_TIMEOUT_MS = 1_000;
const STOP_TIMEOUT_MS = 3_000;

export interface CapturedAudio {
  blob: Blob;
  /** What MediaRecorder actually produced, not what we asked for. */
  mimeType: string;
  durationMs: number;
}

export interface CaptureHooks {
  /** Called with the live recorder, then with null once it is finished. */
  onRecorder?: (recorder: MediaRecorder | null) => void;
}

interface RecorderErrorEventLike {
  error?: unknown;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Record `stream` for exactly as long as `body` takes.
 *
 * Always stops the recorder, even when `body` throws — an abandoned
 * MediaRecorder holds the mic open and the next take captures nothing.
 */
export async function captureWhile(
  stream: MediaStream,
  requestedMimeType: string | null,
  body: () => Promise<void>,
  hooks: CaptureHooks = {},
): Promise<CapturedAudio> {
  let recorder: MediaRecorder;
  try {
    recorder = requestedMimeType
      ? new MediaRecorder(stream, { mimeType: requestedMimeType })
      : new MediaRecorder(stream);
  } catch (err) {
    throw new RecorderError("recorder-failed", "This browser refused to start recording.", err);
  }

  hooks.onRecorder?.(recorder);
  const parts: Blob[] = [];
  let failure: unknown = null;

  recorder.ondataavailable = (event: BlobEvent) => {
    if (event.data && event.data.size > 0) parts.push(event.data);
  };

  const started = new Promise<void>((resolve) => {
    recorder.onstart = () => resolve();
  });
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
    recorder.onerror = (event: Event) => {
      failure = (event as unknown as RecorderErrorEventLike).error ?? event;
      resolve();
    };
  });

  const startedAt = performance.now();
  try {
    recorder.start();
    await Promise.race([started, delay(START_TIMEOUT_MS)]);
    await body();
  } finally {
    if (recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        /* Already stopping. */
      }
    }
    await Promise.race([stopped, delay(STOP_TIMEOUT_MS)]);
    recorder.ondataavailable = null;
    recorder.onstart = null;
    recorder.onstop = null;
    recorder.onerror = null;
    hooks.onRecorder?.(null);
  }

  if (failure) {
    throw new RecorderError("recorder-failed", "The recorder failed mid-take.", failure);
  }

  const mimeType = recorder.mimeType || requestedMimeType || "audio/webm";
  const blob = new Blob(parts, { type: mimeType });

  if (blob.size === 0) {
    throw new RecorderError(
      "no-audio-captured",
      "Nothing was recorded. Check the microphone isn't muted or in use by another app.",
    );
  }

  return { blob, mimeType, durationMs: performance.now() - startedAt };
}
